// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 27-fleet-health-monitor.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Download } from 'lucide-react';
import { toast } from 'sonner';

import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { downloadTextFile } from '@/lib/download';

/** One page of a stored log: its text and the cursor to the page after it. */
export interface LogPage {
  readonly text: string;
  readonly next_cursor: string | null;
}

/** A page read as the host surface performs it: React Query on Internal Health, its own fetch on the fleet. */
export interface LogPageRead<P extends LogPage> {
  readonly page: P | null;
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly isFetching: boolean;
  readonly refetch: () => void;
}

/**
 * Where a log comes from. `usePage` is a hook, called once per render with the
 * cursor of the page the viewer is waiting on; `readAll` assembles the whole
 * log for Download and throws when it cannot.
 */
export interface LogSource<P extends LogPage = LogPage> {
  readonly usePage: (cursor: string | undefined) => LogPageRead<P>;
  readonly readAll: () => Promise<string>;
}

export interface LogViewerProps<P extends LogPage> {
  readonly source: LogSource<P>;
  /** False when nothing was captured; the viewer then shows `emptyCopy` and reads nothing. */
  readonly hasLog: boolean;
  readonly emptyCopy: string;
  /** What the viewer is showing, truncation included, from the newest page read when there is one. */
  readonly caption: (page: P | null) => string;
  readonly downloadName: string;
  readonly label: string;
  /** What the log is called in the viewer's own copy: "transcript", "log". */
  readonly noun?: string;
  readonly testId?: string;
  readonly emptyTestId?: string;
}

/**
 * A stored log, paged in on demand, downloadable whole.
 *
 * Rendered as one `<pre>` text node, not a virtualized list of lines. The log's
 * whole point is that a reader can search it, and the browser's own
 * find-in-page cannot see rows a virtualizer has not mounted; a single text
 * node also costs one DOM node for two megabytes instead of tens of thousands.
 *
 * Nothing in the text is interpreted. It is untrusted command output, so no
 * ANSI decoding, no markup, no links: React escapes it and any escape codes
 * appear as the inert characters they are.
 */
export function LogViewer<P extends LogPage>({
  source,
  hasLog,
  emptyCopy,
  caption,
  downloadName,
  label,
  noun = 'log',
  testId = 'log-viewer',
  emptyTestId = 'log-viewer-empty',
}: LogViewerProps<P>): React.JSX.Element {
  const [cursor, setCursor] = React.useState<string | undefined>(undefined);
  const [text, setText] = React.useState('');
  const [nextCursor, setNextCursor] = React.useState<string | null>(null);

  const read = source.usePage(cursor);
  const page = read.page;

  // Pages accumulate rather than replace: the viewer is one growing document,
  // and each cursor is applied at most once so a refetch of a page already
  // appended does not duplicate it.
  const appended = React.useRef(new Set<string>());
  React.useEffect(() => {
    if (page === null) return;
    const key = cursor ?? 'first';
    if (appended.current.has(key)) return;
    appended.current.add(key);
    setText((loaded) => loaded + page.text);
    setNextCursor(page.next_cursor);
  }, [page, cursor]);

  const [downloading, setDownloading] = React.useState(false);

  /**
   * Download reads the whole log itself rather than reusing the viewer's
   * accumulator: a reader can ask for the file before scrolling to the end,
   * and a partial file that looks complete is worse than a slower one.
   */
  const download = async (): Promise<void> => {
    setDownloading(true);
    try {
      downloadTextFile(downloadName, await source.readAll(), 'text/plain');
    } catch {
      toast.error(`Couldn’t download this ${noun}. Retry in a moment.`);
    } finally {
      setDownloading(false);
    }
  };

  if (!hasLog) {
    return (
      <p className="text-sm text-muted-foreground" data-testid={emptyTestId}>
        {emptyCopy}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">{caption(page)}</p>
        <Button
          variant="outline"
          size="sm"
          className="h-11 md:h-8"
          disabled={downloading}
          onClick={() => void download()}
        >
          <Download aria-hidden="true" className="mr-1.5 size-3.5" />
          {downloading ? 'Preparing…' : 'Download'}
        </Button>
      </div>

      {read.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Couldn&rsquo;t load this {noun}. Retry in a moment.</AlertDescription>
        </Alert>
      ) : null}
      {read.isLoading && text === '' ? (
        <Skeleton className="h-64 w-full" />
      ) : text !== '' || !read.isError ? (
        <pre
          data-testid={testId}
          aria-label={label}
          tabIndex={0}
          className="max-h-[60vh] overflow-auto rounded-[8px] border border-border bg-muted/40 p-3 font-mono text-[11px] leading-[1.55] text-foreground"
        >
          {text}
        </pre>
      ) : null}

      {nextCursor !== null && (
        <Button
          variant="outline"
          size="sm"
          className="h-11 w-full md:h-8"
          disabled={read.isFetching}
          onClick={() => {
            if (cursor === nextCursor) {
              read.refetch();
              return;
            }
            setCursor(nextCursor);
          }}
        >
          {read.isFetching ? 'Loading…' : `Load more of the ${noun}`}
        </Button>
      )}
    </div>
  );
}

export interface LogDrawerProps<P extends LogPage> {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly title: React.ReactNode;
  readonly srDescription: string;
  readonly storageKey: string;
  readonly testId?: string;
  /** What the log belongs to, rendered above it: outcome, phases, the record's own fields. */
  readonly children?: React.ReactNode;
  /**
   * The log itself, once the record it belongs to is known. `recordId` identifies the
   * record, so opening another one starts a fresh viewer rather than appending
   * to the previous record's text.
   */
  readonly log?:
    | (LogViewerProps<P> & { readonly recordId: string; readonly heading: string })
    | null;
}

/** A record and its stored log, side by side with the page that opened it. */
export function LogDrawer<P extends LogPage>({
  open,
  onClose,
  title,
  srDescription,
  storageKey,
  testId,
  children,
  log,
}: LogDrawerProps<P>): React.JSX.Element {
  return (
    <GLDrawer
      open={open}
      onOpenChange={(next) => !next && onClose()}
      variant="panel"
      defaultWidth={720}
      minWidth={380}
      storageKey={storageKey}
      title={title}
      srDescription={srDescription}
      {...(testId ? { contentProps: { 'data-testid': testId } } : {})}
    >
      <div className="space-y-5">
        {children}
        {log ? <LogSection key={log.recordId} {...log} /> : null}
      </div>
    </GLDrawer>
  );
}

function LogSection<P extends LogPage>({
  heading,
  recordId: _recordId,
  ...viewer
}: LogViewerProps<P> & { readonly recordId: string; readonly heading: string }): React.JSX.Element {
  return (
    <section className="space-y-2">
      <h3 className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
        {heading}
      </h3>
      <LogViewer {...viewer} />
    </section>
  );
}
